import type { StageId } from './stages'

/**
 * A bucket is a saved filter plus the stage we are trying to move people to.
 *
 * The filter is a small typed AST rather than raw SQL so that it can be
 * evaluated in three places from one definition: in memory (tests, previews),
 * compiled to SQL (counting and listing), and — in Phase 2 — translated into a
 * Klaviyo segment. Raw SQL in a text column would make the second and third
 * impossible.
 */

export const BUCKET_FIELDS = [
  'stage',
  'channel',
  'practiceId',
  'sessionsTotal',
  'daysSincePaid',
  'daysSinceLastSession',
  'daysSinceSignup',
  'emailOpens30d',
  'hasPhone',
  'hasEmailConsent',
  'rx',
  'ageRange',
  /** Essentials programme: its length, and how much of it is left to do. */
  'essentialsSessions',
  'essentialsClass',
  'essentialsRemaining',
  'essentialsProgress',
  'isTest',
  'isSuperseded',
  'isEmployee',
  'isOldEnrolment',
  'isSpam',
  /** On the hand-kept VIP list. Set by a person, never derived. */
  'isVip',
  /** An optometrist we work with rather than a customer. */
  'isOptometrist',
  /** Tablet sent out, and whether it has come back. */
  'tabletReceived',
  'tabletReturned',
  /** How the money arrived: 'wix' | 'quickbooks' | 'wire' | 'shopify' | 'optometrist'. */
  'paymentChannel',
  'paidCents',
  /** What a practice's patient is to them: 'test' | 'team' | 'paying'. */
  'optometristUserType',
  /** Who did the eye check, as `person_projection` resolves it. */
  'examinedBy',
  /** Has a Wix order of $1 or less — the line below which an order is a team test. */
  'hasTestOrder',
  /** Where they signed up, as Klaviyo or the waitlist form recorded it ('timferriss', 'WP', a page URL). */
  'signupSource',
  /** The promo the waitlist form carried ('TF_Sep_26'). */
  'signupPromo',
] as const

export type BucketField = (typeof BUCKET_FIELDS)[number]

export type Comparison =
  | { field: BucketField; op: 'eq' | 'neq'; value: string | number | boolean | null }
  | { field: BucketField; op: 'gt' | 'gte' | 'lt' | 'lte'; value: number }
  | { field: BucketField; op: 'in' | 'nin'; value: Array<string | number> }
  | { field: BucketField; op: 'isNull' | 'notNull' }

/**
 * "Has an order that...": every part must hold for the same order, so a $1 test
 * order and a $999 app order never add up to "bought the kit at $999". Only
 * orders that count as a sale are looked at: paid, not refunded, not a test.
 * Dates are calendar days in UTC, `paidFrom` inclusive and `paidBefore`
 * exclusive, so 20 Oct to 1 Nov is October from the 20th on.
 */
export interface OrderMatch {
  /** Store catalog ids; any one of them on the order. A renamed product keeps its id. */
  products?: string[]
  minCents?: number
  maxCents?: number
  /** YYYY-MM-DD */
  paidFrom?: string
  /** YYYY-MM-DD */
  paidBefore?: string
  /** Discount codes, compared without regard to case. */
  codes?: string[]
}

export type BucketRule =
  | Comparison
  | { order: OrderMatch }
  | { all: BucketRule[] }
  | { any: BucketRule[] }
  | { not: BucketRule }

/** One order as a rule sees it. */
export interface OrderSubject {
  productIds: string[]
  amountCents: number
  /** ISO timestamp. */
  paidAt: string
  code: string | null
  /** Paid, not refunded, not a test: the only orders an order rule looks at. */
  counts: boolean
}

export function orderMatches(m: OrderMatch, o: OrderSubject): boolean {
  if (!o.counts) return false
  if (m.products?.length && !o.productIds.some((id) => m.products!.includes(id))) return false
  if (m.minCents != null && o.amountCents < m.minCents) return false
  if (m.maxCents != null && o.amountCents > m.maxCents) return false
  const day = o.paidAt.slice(0, 10)
  if (m.paidFrom && day < m.paidFrom) return false
  if (m.paidBefore && day >= m.paidBefore) return false
  if (m.codes?.length) {
    const code = o.code?.toLowerCase()
    if (!code || !m.codes.some((c) => c.toLowerCase() === code)) return false
  }
  return true
}

/** The flattened person projection a rule is evaluated against. */
export interface BucketSubject {
  stage: StageId
  channel: string
  practiceId: string | null
  sessionsTotal: number
  daysSincePaid: number | null
  daysSinceLastSession: number | null
  daysSinceSignup: number | null
  emailOpens30d: number
  hasPhone: boolean
  hasEmailConsent: boolean
  rx: string | null
  ageRange: string | null
  essentialsSessions: number | null
  essentialsClass: string | null
  essentialsRemaining: number | null
  /** 0.5 is half the assigned programme. Can exceed 1. */
  essentialsProgress: number | null
  isTest: boolean
  isSuperseded: boolean
  isEmployee: boolean
  isOldEnrolment: boolean
  isSpam: boolean
  isVip: boolean
  isOptometrist: boolean
  tabletReceived: boolean
  tabletReturned: boolean
  paymentChannel: string | null
  optometristUserType: string | null
  examinedBy: string | null
  /** Money that counted, in cents. Test and demo orders are already excluded. */
  paidCents: number
  /** Has a Wix order of $1 or less, which never counts as money. */
  hasTestOrder?: boolean
  signupSource?: string | null
  signupPromo?: string | null
  /** Every order, counted or not; order rules filter them. */
  orders?: OrderSubject[]
}

export interface BucketDefinition {
  id: string
  name: string
  description?: string
  kind: 'dynamic' | 'static'
  rule: BucketRule
  /**
   * The stage this bucket is trying to move people to. This is what turns a
   * campaign into a measurable experiment: success is the share of members who
   * reached goalStage, not the open rate.
   */
  goalStage: StageId | null
  goalNote?: string
}

export function evaluateRule(rule: BucketRule, subject: BucketSubject): boolean {
  if ('all' in rule) return rule.all.every((r) => evaluateRule(r, subject))
  if ('any' in rule) return rule.any.some((r) => evaluateRule(r, subject))
  if ('not' in rule) return !evaluateRule(rule.not, subject)
  if ('order' in rule) return (subject.orders ?? []).some((o) => orderMatches(rule.order, o))

  const actual = subject[rule.field] as unknown

  switch (rule.op) {
    case 'isNull':
      return actual === null || actual === undefined
    case 'notNull':
      return actual !== null && actual !== undefined
    case 'eq':
      return actual === rule.value
    case 'neq':
      return actual !== rule.value
    case 'in':
      return actual !== null && rule.value.includes(actual as string | number)
    case 'nin':
      return actual === null || !rule.value.includes(actual as string | number)
    case 'gt':
    case 'gte':
    case 'lt':
    case 'lte': {
      // A null numeric field is "unknown", never a match. Treating null as 0
      // would put every waitlist lead into every "days since payment" bucket.
      if (typeof actual !== 'number') return false
      if (rule.op === 'gt') return actual > rule.value
      if (rule.op === 'gte') return actual >= rule.value
      if (rule.op === 'lt') return actual < rule.value
      return actual <= rule.value
    }
  }
}

/** Human-readable rendering, shown on the bucket card so the rule is never a mystery. */
export function describeRule(rule: BucketRule): string {
  if ('all' in rule) return rule.all.map(describeRule).join(' AND ')
  if ('any' in rule) return `(${rule.any.map(describeRule).join(' OR ')})`
  if ('not' in rule) return `NOT (${describeRule(rule.not)})`
  if ('order' in rule) return describeOrder(rule.order)

  const f = snake(rule.field)
  switch (rule.op) {
    case 'isNull':
      return `${f} is empty`
    case 'notNull':
      return `${f} is set`
    case 'in':
      return `${f} in (${rule.value.join(', ')})`
    case 'nin':
      return `${f} not in (${rule.value.join(', ')})`
    case 'eq':
      return `${f} = ${rule.value}`
    case 'neq':
      return `${f} ≠ ${rule.value}`
    case 'gt':
      return `${f} > ${rule.value}`
    case 'gte':
      return `${f} ≥ ${rule.value}`
    case 'lt':
      return `${f} < ${rule.value}`
    case 'lte':
      return `${f} ≤ ${rule.value}`
  }
}

/**
 * Whether a rule mentions a field anywhere in its tree.
 *
 * Used for exactly one thing: a bucket that asks for `isTest` is asking to see
 * hidden people, so it reads the unfiltered relation. Every other bucket reads
 * the filtered one. That keeps "hidden everywhere" true without making the
 * Test users bucket a special case anyone has to remember.
 */
/**
 * Fields that hide a person from every screen. A bucket mentioning any of them
 * is asking to see hidden people, and reads the unfiltered relation.
 */
/**
 * Fields that only exist on the unfiltered relation.
 *
 * A bucket naming one of these is asking about people the patient list hides, so
 * it reads `person_projection_all` instead. Without that the bucket counts
 * correctly — the worker always reads the wide relation — and then opens to an
 * empty list, which is a worse failure than not existing: the count says the
 * people are there and the screen says they are not.
 */
export const HIDDEN_FIELDS = [
  'isTest',
  'isSuperseded',
  'isOldEnrolment',
  'isSpam',
  /* A $1 order is a team test purchase, and the people who place them are
     nearly all test accounts; a bucket about them that hid test accounts would
     be empty. */
  'hasTestOrder',
] as const satisfies readonly BucketField[]

export function ruleWantsHidden(rule: BucketRule): boolean {
  return HIDDEN_FIELDS.some((f) => ruleMentionsField(rule, f))
}

export function ruleMentionsField(rule: BucketRule, field: BucketField): boolean {
  if ('all' in rule) return rule.all.some((r) => ruleMentionsField(r, field))
  if ('any' in rule) return rule.any.some((r) => ruleMentionsField(r, field))
  if ('not' in rule) return ruleMentionsField(rule.not, field)
  if ('order' in rule) return false
  return rule.field === field
}

function describeOrder(m: OrderMatch): string {
  const parts: string[] = []
  if (m.products?.length) parts.push(`product in (${m.products.join(', ')})`)
  if (m.minCents != null) parts.push(`total ≥ $${(m.minCents / 100).toFixed(2)}`)
  if (m.maxCents != null) parts.push(`total ≤ $${(m.maxCents / 100).toFixed(2)}`)
  if (m.paidFrom) parts.push(`paid from ${m.paidFrom}`)
  if (m.paidBefore) parts.push(`paid before ${m.paidBefore}`)
  if (m.codes?.length) parts.push(`code in (${m.codes.join(', ')})`)
  return parts.length ? `has an order with ${parts.join(', ')}` : 'has a paid order'
}

function snake(s: string): string {
  return s.replace(/[A-Z]/g, (c) => '_' + c.toLowerCase())
}

/**
 * The starter set from the spec. These ship with the product because they are
 * the buckets every subscription business needs on day one, and because having
 * real examples is what makes the bucket editor comprehensible.
 */
export const STARTER_BUCKETS: readonly Omit<BucketDefinition, 'id'>[] = [
  {
    name: 'Paid 7+ days, no account yet',
    description: 'Money taken, nothing delivered. The most urgent queue in the system.',
    kind: 'dynamic',
    goalStage: 'activated_not_started',
    rule: { all: [{ field: 'stage', op: 'eq', value: 'paid_not_activated' }, { field: 'daysSincePaid', op: 'gte', value: 7 }] },
  },
  {
    name: 'Account created, never trained',
    description: 'Onboarding friction — wrong device, unclear instructions, or no reminder.',
    kind: 'dynamic',
    goalStage: 'onboarding',
    rule: { all: [{ field: 'stage', op: 'eq', value: 'activated_not_started' }, { field: 'daysSincePaid', op: 'gte', value: 3 }] },
  },
  {
    name: 'Onboarding stall',
    description: 'Started but losing the daily habit before it formed.',
    kind: 'dynamic',
    goalStage: 'training_active',
    rule: { all: [{ field: 'stage', op: 'eq', value: 'onboarding' }, { field: 'daysSinceLastSession', op: 'gte', value: 3 }] },
  },
  {
    name: 'High-investment win-back',
    description: 'Already banked 10+ sessions. The best win-backs you have.',
    kind: 'dynamic',
    goalStage: 'training_active',
    rule: { all: [{ field: 'stage', op: 'eq', value: 'at_risk' }, { field: 'sessionsTotal', op: 'gte', value: 10 }] },
  },
  {
    name: 'Training first half',
    description: 'Started the Essentials programme and is under halfway through it.',
    kind: 'dynamic',
    goalStage: 'training_active',
    goalNote: 'keep the habit until the halfway mark',
    /* `essentialsProgress` is null for anyone without an assigned programme, and
       a null numeric never matches, so this needs no separate "has a programme"
       clause. Exactly half belongs to the second half, not this one: the brief
       says "did not complete at least half". */
    rule: {
      all: [
        { field: 'sessionsTotal', op: 'gte', value: 1 },
        { field: 'essentialsProgress', op: 'lt', value: 0.5 },
      ],
    },
  },
  {
    name: 'Training second half',
    description: 'Past the halfway mark of the Essentials programme and not finished yet.',
    kind: 'dynamic',
    goalStage: 'completed',
    goalNote: 'carry them to the end of the programme',
    /* Strictly under 1: someone who has met or passed their assigned count has
       finished, and 29 of the 71 assigned people are already there. */
    rule: {
      all: [
        { field: 'essentialsProgress', op: 'gte', value: 0.5 },
        { field: 'essentialsProgress', op: 'lt', value: 1 },
      ],
    },
  },
  {
    name: 'Essentials complete',
    description: 'Did every session the Essentials programme assigned them, or more.',
    kind: 'dynamic',
    goalStage: null,
    goalNote: 'assessment · testimonial · referral',
    /* `gte 1` rather than `eq 1`: the assigned count is a plan, not a ceiling,
       and people run past it — one is at 2.06 of theirs. Distinct from
       "Protocol complete", which is the stage engine's verdict and needs a
       payment date this source does not carry. */
    rule: { field: 'essentialsProgress', op: 'gte', value: 1 },
  },
  {
    name: 'No programme assigned',
    description: 'No Essentials programme on record — the older methodology, or not yet sized.',
    kind: 'dynamic',
    goalStage: null,
    goalNote: 'size them, or confirm they predate the methodology',
    /* Keyed on the session count rather than the class, so the handful whose
       class could not be settled land here too: an uncertain programme length
       is not a programme length you can plan against. */
    rule: { field: 'essentialsSessions', op: 'isNull' },
  },
  {
    name: 'Original enrolments',
    description: 'Earlier accounts — superseded by a newer one, or marked OLD in Oryx. Hidden from the patients list, kept for comparison.',
    kind: 'dynamic',
    goalStage: null,
    goalNote: 'the training that came before the current programme',
    /* Test accounts are excluded explicitly. Naming a hidden field makes this
       bucket read the unfiltered relation, which is what lets it see superseded
       people at all — but that also lifts the automatic test exclusion every
       other bucket gets, so it has to say so itself. */
    rule: {
      all: [
        /* Either kind of earlier enrolment: one that points at its replacement,
           or one Oryx only marked "(OLD)" in the name because no replacement was
           ever recorded. Both are hidden from the population; this is where they
           stay reachable. */
        { any: [
          { field: 'isSuperseded', op: 'eq', value: true },
          { field: 'isOldEnrolment', op: 'eq', value: true },
        ] },
        { field: 'isTest', op: 'eq', value: false },
      ],
    },
  },
  {
    name: 'Spam',
    description: 'Junk form submissions. Hidden everywhere; this is where to find one that was marked by mistake.',
    kind: 'dynamic',
    goalStage: null,
    goalNote: 'nothing — this bucket exists to make the hiding reversible',
    /* Naming a hidden field makes this bucket read the unfiltered relation,
       which is the only way it can see what it is about. */
    rule: { field: 'isSpam', op: 'eq', value: true },
  },
  {
    name: 'VIPs',
    description:
      'The patients somebody is personally looking after. Marked by hand on the record — nothing about what they paid or how much they train puts anybody here.',
    kind: 'dynamic',
    goalStage: null,
    goalNote: 'nobody on this list should go quiet without somebody noticing',
    rule: { field: 'isVip', op: 'eq', value: true },
  },
  {
    name: 'Tablet out',
    description:
      'Patients holding one of our tablets who have not sent it back. Hardware we are waiting on.',
    kind: 'dynamic',
    goalStage: null,
    goalNote: 'know what is out there, and get it back when the programme ends',
    rule: {
      all: [
        { field: 'tabletReceived', op: 'eq', value: true },
        { field: 'tabletReturned', op: 'eq', value: false },
      ],
    },
  },
  {
    name: 'Tablet returned',
    description: 'Patients who were sent a tablet and have returned it.',
    kind: 'dynamic',
    goalStage: null,
    goalNote: 'closed out — the hardware is back',
    rule: { field: 'tabletReturned', op: 'eq', value: true },
  },
  {
    name: 'Bar Ilan study',
    description: 'Patients recruited through the Bar Ilan study rather than a practice. A research cohort, not customers.',
    kind: 'dynamic',
    goalStage: null,
    goalNote: 'keep the study population separable from the market',
    /* Matched on the examiner rather than the practice id: the study arrives
       under four different practice rows — "Bar Ilan", "Bar Ilan-Other" and one
       per recruiter — and they all resolve to the same examiner. */
    rule: { field: 'examinedBy', op: 'eq', value: 'Bar Ilan' },
  },
  {
    name: 'Paid — optometrist',
    description: 'Patients a practice has told us are paying, whatever they bought. The practice, not Wix, is where the money came from.',
    kind: 'dynamic',
    goalStage: null,
    goalNote: 'the revenue each practice actually produces',
    /* Reads the classification a human sets on the practice page, not a payment
       record: an optometrist bills their own patient, so Joker never sees an
       order for them and `paidCents` stays zero. */
    rule: { field: 'optometristUserType', op: 'eq', value: 'paying' },
  },
  {
    name: 'Paid — all sources',
    description: 'Everyone who has paid, however they paid: the Wix store, an optometrist, and whatever comes next.',
    kind: 'dynamic',
    goalStage: null,
    goalNote: 'the paying population, counted once',
    /* An `any` of one clause per route to money, so adding a source is adding a
       clause rather than rewriting the rule. Somebody who bought on Wix *and*
       is a practice's paying patient is one person here, not two. */
    rule: {
      any: [
        { field: 'paidCents', op: 'gt', value: 0 },
        { field: 'optometristUserType', op: 'eq', value: 'paying' },
        /* A payment date with no amount behind it: the money arrived, nobody
           wrote down how much. */
        { field: 'daysSincePaid', op: 'notNull' },
      ],
    },
  },
  {
    name: 'Paid on Wix',
    description:
      'Bought the product through the Wix store. Test charges of a unit or less are excluded, and so is anyone who paid another way.',
    kind: 'dynamic',
    goalStage: 'activated_not_started',
    goalNote: 'get them into the app',
    /* Tests how the money arrived, not merely that it did. This used to be
       `paidCents > 0`, which was the same thing back when Wix was the only
       route an order could come from — by the time there were bank transfers
       and QuickBooks invoices it put twelve people who had never touched the
       store into a bucket named after it, Lior Shoham's bank transfer among
       them. */
    rule: { field: 'paymentChannel', op: 'eq', value: 'wix' },
  },
  {
    name: 'Employees',
    description: 'Staff training on the product themselves. Counted in the population, labelled so they can be separated.',
    kind: 'dynamic',
    goalStage: null,
    goalNote: 'exclude from customer benchmarks',
    /* Not a hidden field: this bucket reads the same relation every other
       bucket does, because employees are part of the population. */
    rule: { field: 'isEmployee', op: 'eq', value: true },
  },
  {
    name: '$1 orders',
    description: 'Everyone with a Wix order of $1 or less. Joker treats those as team test purchases: kept in the ledger, never counted as a sale.',
    kind: 'dynamic',
    goalStage: null,
    goalNote: 'where test checkouts can be checked',
    rule: { field: 'hasTestOrder', op: 'eq', value: true },
  },
  {
    name: 'Test users',
    description: 'Hidden from every screen — both the name-matched accounts and the ones marked by hand.',
    kind: 'dynamic',
    goalStage: null,
    goalNote: 'the only way to reach a hidden account',
    /* Mentioning `isTest` is what tells the query layer to read the unfiltered
       relation, so this is the one bucket that can see hidden people. */
    rule: { field: 'isTest', op: 'eq', value: true },
  },
  {
    name: 'Protocol complete',
    description: 'Finished the course. Ask for the assessment, the testimonial and the referral.',
    kind: 'dynamic',
    goalStage: null,
    goalNote: 'assessment · testimonial · referral',
    rule: { field: 'stage', op: 'eq', value: 'completed' },
  },
  {
    name: 'Warm long-term waitlist',
    description: 'Still opening email months later. Interested, not yet converted.',
    kind: 'dynamic',
    goalStage: 'paid_not_activated',
    rule: { all: [{ field: 'stage', op: 'eq', value: 'lead' }, { field: 'daysSinceSignup', op: 'gte', value: 60 }, { field: 'emailOpens30d', op: 'gte', value: 3 }] },
  },
  {
    name: 'Optometrist-referred, stuck',
    description: 'Route these to the practice that sold them, not to a generic email.',
    kind: 'dynamic',
    goalStage: 'onboarding',
    goalNote: 'route to the referring practice',
    rule: {
      all: [
        { field: 'channel', op: 'eq', value: 'optometrist' },
        { field: 'stage', op: 'in', value: ['paid_not_activated', 'activated_not_started'] },
      ],
    },
  },
] as const
